import { Checkbox } from '@material-ui/core';
import CSS from 'csstype';
import moment from 'moment';
import React from 'react';

import { Calendar } from '~/src/dash/components/calendar/calendar';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import {
  ITableGroupByForReportsDashboard,
  ITableMetric,
} from '~/src/dash/components/tables/common';
import { Col, Cols, Row, Rows, spacing, Text, TextButton, usePanel } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import { IMenuList } from '~/src/dash/types/menuList';
import {
  generateCalendarQuickSelectorCustomOptions,
  generateCalendarQuickSelectorOptions,
  generateCompareQuickSelectorCustomOptions,
  generateCompareQuickSelectorOptions,
  IDualCalendarProps,
} from '~/src/dash/utils/calendar';
import { formatDateFromUTCToOffset, formatWithoutHoursMins } from '~/src/dash/utils/date';
import { IIASDashboardFilter } from '~/src/dash/views/ias-publisher/IASDashboard/common/types';
import { IASDashboardFiltersPill } from '~/src/dash/views/ias-publisher/IASDashboard/IASDashboardFilters/IASDashboardFiltersPill';
import { ISingleSelectOption, SingleSelect } from '~/src/dash/views/singleselect-view';

interface IFilterLabelProps {
  svgHeight?: number;
  svgPath: string;
  svgStyle?: CSS.Properties;
  title: string;
  titleStyle?: CSS.Properties;
}

const FilterLabel = ({
  svgHeight,
  svgPath,
  svgStyle = {},
  title,
  titleStyle = {},
}: IFilterLabelProps) => {
  return (
    <Cols style={{ alignItems: 'center' }}>
      <Col style={{ ...svgStyle }}>
        <img src={svgPath} height={svgHeight ? `${svgHeight}px` : 'inherit'} />
      </Col>
      <Col
        style={{
          paddingLeft: spacing.small,
          paddingRight: spacing.medium,
          ...titleStyle,
        }}
      >
        <Text small bold>
          {title}
        </Text>
      </Col>
    </Cols>
  );
};

interface IReportsDashboardFiltersProps extends IDualCalendarProps, IStore {
  timezone: string;
  timezonesList: IMenuList;
  handleTimezoneOnChange: (list: IMenuList) => void;
  filters: IIASDashboardFilter[];
  isClearFiltersEnabled: boolean;
  groupBySelectItems: ISingleSelectOption[];
  currentGroupBy: ITableGroupByForReportsDashboard;
  handleGroupByOnChange: (name: string, value: string | string[] | number) => void;
  orderBySelectItems: ISingleSelectOption[];
  orderByList: IMenuList;
  currentOrderBy: ITableMetric;
  thresholdSelectedItems: ISingleSelectOption[];
  currentThreshold: string | number;
  handleOrderByOnChange: (name: string, value: string | string[] | number) => void;
  handleOrderByListOnChange: (list: IMenuList) => void;
  handleThresholdOnChange: (name: string, value: string | string[] | number) => void;
  handleFilterOnChange: (menuTitle: string) => (values: IMenuList) => void;
  handleFilterAccept: () => void;
  handleClearFilters: () => void;
}

export const ReportsDashboardFilters = ({
  session,
  timezone,
  timezonesList,
  handleTimezoneOnChange,
  // calendar props
  range1Selected,
  range1DisplaySelected,
  range2Selected,
  range2DisplaySelected,
  compStartDate,
  compEndDate,
  compDisplayStartDate,
  compDisplayEndDate,
  calStartDate,
  calEndDate,
  calDisplayStartDate,
  calDisplayEndDate,
  calFocusedInput,
  compFocusedInput,
  isComparedToEnabled,
  // calendar functions
  handleChangeAggRangeSelected,
  handleCalendarDateChange,
  handleDateRangeChange,
  handleCalendarFocusedInputChange,
  handleCompareEnabled,
  // filter props
  filters,
  isClearFiltersEnabled,
  groupBySelectItems,
  currentGroupBy,
  orderBySelectItems,
  currentOrderBy,
  orderByList,
  thresholdSelectedItems,
  currentThreshold,
  handleGroupByOnChange,
  handleOrderByOnChange,
  handleOrderByListOnChange,
  handleThresholdOnChange,
  handleFilterOnChange,
  handleFilterAccept,
  handleClearFilters,
}: IReportsDashboardFiltersProps) => {
  const { colors } = usePanel();

  // calendar consts
  const compareQuickSelectorOptions = generateCompareQuickSelectorOptions({
    timezone,
    startDate: calDisplayStartDate,
    endDate: calDisplayEndDate,
    rangeSelected: range1DisplaySelected,
  });
  const compareQuickSelectorCustomOptions = generateCompareQuickSelectorCustomOptions({
    timezone,
    startDate: calDisplayStartDate,
    endDate: calDisplayEndDate,
  });
  const isCalDayHighlighted = (day: moment.Moment): boolean => {
    if (!isComparedToEnabled) {
      return false;
    }
    return formatDateFromUTCToOffset(day.format('YYYY-MM-DDTHH:mm:ss'), 'UTC').isBetween(
      compDisplayStartDate,
      compDisplayEndDate,
      'day',
      '[]',
    );
  };
  const isCompDayHighlighted = (day: moment.Moment): boolean => {
    return formatDateFromUTCToOffset(day.format('YYYY-MM-DDTHH:mm:ss'), 'UTC').isBetween(
      calDisplayStartDate,
      calDisplayEndDate,
      'day',
      '[]',
    );
  };

  return (
    <Rows>
      {/* CALENDAR */}
      <Row
        displayFlex
        style={{
          minHeight: '40px',
          borderBottom: `1px solid ${colors.lightGray}`,
          padding: '0px 16px',
        }}
      >
        <Cols
          max
          style={{
            position: 'relative',
            alignItems: 'center',
          }}
        >
          <Col style={{ width: '80px' }}>
            <FilterLabel
              svgPath='/assets/shared/svg/Calendar15px.svg'
              title='Period'
              svgStyle={{ paddingTop: spacing.extraSmall }}
            />
          </Col>
          <Col>
            <Cols style={{ alignItems: 'center' }}>
              <Col>
                <Text extraSmall>{range1DisplaySelected}</Text>
              </Col>
              <Col style={{ paddingLeft: spacing.small }}>
                <Calendar
                  startDate={calStartDate}
                  endDate={calEndDate}
                  calStartDate={calDisplayStartDate}
                  calEndDate={calDisplayEndDate}
                  startDateID='startDate'
                  endDateID='endDate'
                  rangeSelected={range1Selected}
                  handleRangeSelected={handleChangeAggRangeSelected}
                  handleCalendarDateChange={handleCalendarDateChange(false)}
                  handleDateRangeChange={handleDateRangeChange}
                  quickSelectorOptions={generateCalendarQuickSelectorOptions(timezone)}
                  quickSelectorCustomOptions={generateCalendarQuickSelectorCustomOptions(timezone)}
                  isDayHighlighted={isCalDayHighlighted}
                  isCompareCalendar={false}
                  focusedInput={calFocusedInput || null}
                  excludeToday={true}
                  dateFormat={formatWithoutHoursMins(session.publisher.Localization)}
                  handleFocusedInputChange={handleCalendarFocusedInputChange(false)}
                  trackOnClose={() => null}
                  timezone={timezone}
                  anchorDirection='left'
                />
              </Col>
              <Col style={{ paddingLeft: spacing.small }}>
                <Checkbox
                  size='small'
                  checked={isComparedToEnabled}
                  onChange={handleCompareEnabled}
                />
              </Col>
              <Col>
                <Text extraSmall>{range2DisplaySelected}</Text>
              </Col>
              <Col style={{ paddingLeft: spacing.small }}>
                <Calendar
                  startDate={compStartDate}
                  endDate={compEndDate}
                  calStartDate={compDisplayStartDate}
                  calEndDate={compDisplayEndDate}
                  startDateID='startDate'
                  endDateID='endDate'
                  rangeSelected={range2Selected}
                  handleRangeSelected={handleChangeAggRangeSelected}
                  handleCalendarDateChange={handleCalendarDateChange(true)}
                  handleDateRangeChange={handleDateRangeChange}
                  quickSelectorOptions={compareQuickSelectorOptions}
                  quickSelectorCustomOptions={compareQuickSelectorCustomOptions}
                  isDayHighlighted={isCompDayHighlighted}
                  isCompareCalendar={true}
                  focusedInput={compFocusedInput || null}
                  dateFormat={formatWithoutHoursMins(session.publisher.Localization)}
                  handleFocusedInputChange={handleCalendarFocusedInputChange(true)}
                  trackOnClose={() => null}
                  timezone={timezone}
                  anchorDirection='left'
                />
              </Col>
              {/* TIMEZONES */}
              <Col style={{ paddingLeft: spacing.medium, paddingTop: spacing.small }}>
                <img height='16px' src='/assets/shared/svg/clock.svg' />
              </Col>
              <Col style={{ paddingLeft: spacing.small }}>
                <Text extraSmall>Timezone</Text>
              </Col>
              <Col style={{ paddingLeft: spacing.small }}>
                <MaterialSearchDropdownMulti
                  fontSize='12px'
                  menuTitle='Timezone'
                  list={timezonesList}
                  onChange={handleTimezoneOnChange}
                  leftRightDropdownEnabled={true}
                  enableFixed={true}
                  selectLimit={1}
                  enableMultiSelect={false}
                />
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Row>
      {/* PILLS */}
      <Row
        displayFlex
        style={{
          minHeight: '40px',
          borderBottom: `1px solid ${colors.lightGray}`,
          padding: '0px 16px',
        }}
      >
        <Cols
          max
          style={{
            position: 'relative',
            alignItems: 'center',
            paddingBottom: '2px',
          }}
        >
          <Col style={{ width: '80px' }}>
            <FilterLabel svgPath='/assets/shared/svg/Filter15px.svg' title='Filters' />
          </Col>
          <Col>
            {/* FILTER PILLS */}
            <Cols style={{ alignItems: 'center' }}>
              {/* SET STATIC FILTERS */}
              {filters.map((filter) => (
                <Col style={{ marginLeft: spacing.small }}>
                  <IASDashboardFiltersPill
                    menuTitle={filter.menuTitle}
                    list={filter.list}
                    handleFilterOnChange={handleFilterOnChange}
                    handleFilterAccept={handleFilterAccept}
                  />
                </Col>
              ))}
            </Cols>
          </Col>
          {/* CLEAR BUTTON */}
          {isClearFiltersEnabled && (
            <Col paddingLeft={spacing.medium}>
              <TextButton onClick={handleClearFilters}>
                <Text extraSmall highlighted style={{ paddingTop: '2px' }}>
                  Clear
                </Text>
              </TextButton>
            </Col>
          )}
        </Cols>
      </Row>
      {/* GROUP BYS */}
      <Row
        style={{
          minHeight: '40px',
          padding: '0px 16px',
        }}
      >
        <Cols
          max
          style={{
            position: 'relative',
            alignItems: 'center',
          }}
        >
          <Col style={{ marginTop: '6px', width: '80px' }}>
            <FilterLabel
              svgPath='/assets/shared/svg/Data15px.svg'
              svgStyle={{ marginTop: spacing.extraSmall }}
              title='Data'
            />
          </Col>
          <Col style={{ paddingTop: '5px', paddingRight: spacing.medium }}>
            <Text extraSmall>Dimensions</Text>
          </Col>
          <Col>
            <Cols style={{ alignItems: 'center' }}>
              <Col style={{ paddingRight: spacing.medium }}>
                <SingleSelect
                  name='View By'
                  label=''
                  items={groupBySelectItems}
                  value={currentGroupBy.label}
                  onChange={handleGroupByOnChange}
                  style={{
                    fontSize: '12px',
                    marginTop: '4px',
                    padding: '0px',
                    minWidth: '180px',
                  }}
                  customMenuHeight={440}
                />
              </Col>
            </Cols>
          </Col>
          <Col style={{ paddingTop: '5px', paddingRight: spacing.medium }}>
            <Text extraSmall>Order By</Text>
          </Col>
          <Col>
            <Cols style={{ alignItems: 'center' }}>
              <Col style={{ paddingRight: spacing.medium }}>
                <SingleSelect
                  name='Order By'
                  label=''
                  items={orderBySelectItems}
                  value={currentOrderBy.label}
                  onChange={handleOrderByOnChange}
                  style={{
                    fontSize: '12px',
                    marginTop: '4px',
                    padding: '0px',
                    minWidth: '120px',
                  }}
                />
              </Col>
            </Cols>
          </Col>
          <Col style={{ paddingTop: '5px', paddingRight: spacing.medium }}>
            <Text extraSmall>Metrics</Text>
          </Col>
          <Col>
            <Cols style={{ alignItems: 'center' }}>
              <Col style={{ paddingRight: spacing.medium, paddingTop: spacing.small }}>
                <MaterialSearchDropdownMulti
                  menuTitle='Metrics'
                  fontSize='12px'
                  list={orderByList}
                  onChange={handleOrderByListOnChange}
                  leftRightDropdownEnabled={true}
                  enableFixed={true}
                  enableMultiSelect={true}
                />
              </Col>
            </Cols>
          </Col>
          <Col style={{ paddingTop: '5px', paddingRight: spacing.medium }}>
            <Text extraSmall>Threshold</Text>
          </Col>
          <Col>
            <Cols style={{ alignItems: 'center' }}>
              <Col>
                <SingleSelect
                  name='Threshold'
                  label=''
                  items={thresholdSelectedItems}
                  value={currentThreshold.toString()}
                  onChange={handleThresholdOnChange}
                  disableAlphanumSort
                  style={{
                    fontSize: '12px',
                    marginTop: '4px',
                    padding: '0px',
                    minWidth: '120px',
                  }}
                />
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Row>
    </Rows>
  );
};
